/**
 * Dynamic CDN script loader.
 *
 * Antes index.html cargaba 4 scripts con `defer` independientemente de la
 * ruta: ol.js (192 KB brotli), xlsx (~90 KB), maps Google API (174 KB),
 * videosdk (93 KB). Total ~552 KB encoded + ~344 ms de EvaluateScript en
 * el main thread del boot — TODO descargado y parseado aunque la ruta no
 * los necesite.
 *
 * Este loader inyecta los scripts solo cuando una ruta los requiere via
 * router.beforeEach (ver src/router/index.js). El cache interno evita
 * pedir el mismo script dos veces si el usuario navega entre rutas que
 * comparten dependencias.
 *
 * Usage:
 *   import { loadScript } from '@/util/cdn-loader'
 *   await loadScript('/js/ol.js', 'ol')   // resolves cuando window.ol existe
 *
 * Idempotente: si window[globalCheck] ya esta poblado o si el src ya fue
 * cargado por otra llamada, retorna Promise.resolve() inmediato.
 */

var loaded = new Set()

export function loadScript(src, globalCheck) {
  // Fast path: ya esta cargado o el global ya existe (caso de assets
  // que pre-existian en el bundle estatico antes de la migracion).
  if (loaded.has(src)) return Promise.resolve()
  if (globalCheck && typeof window !== 'undefined' && window[globalCheck]) {
    loaded.add(src)
    return Promise.resolve()
  }

  return new Promise(function(resolve, reject) {
    var el = document.createElement('script')
    el.src = src
    // Sin defer — el script se descarga eager (ya estamos en runtime, no
    // en parse del HTML). Sin async tampoco — order de ejecucion
    // determinista importa cuando un script depende de otro globalmente
    // (ej. ol.js antes que markercluster).
    el.onload = function() {
      loaded.add(src)
      resolve()
    }
    el.onerror = function(err) {
      // Removemos del DOM para que un retry posterior pueda re-intentar
      // (ej. al recargar la ruta despues de un fallo de red transitorio).
      try { document.head.removeChild(el) } catch (_) {}
      reject(err)
    }
    document.head.appendChild(el)
  })
}

/**
 * Carga multiples scripts en paralelo. Resuelve cuando todos terminan;
 * rechaza si alguno falla. Util para grupos relacionados (ej. cargar
 * markercluster solo despues que ol.js este listo no aplica aqui — para
 * dependencias seriales usa await en serie).
 */
export function loadScripts(specs) {
  return Promise.all(specs.map(function(spec) {
    return loadScript(spec.src, spec.globalCheck)
  }))
}
